import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

export type TStatedFieldsProps = {
    /** The record as the API returned it; only what it actually states is rendered. */
    data: Record<string, unknown>
    /** Keys already shown elsewhere on the card, plus the technical ones. */
    skip?: string[]
    size?: 6 | 12
}

const SKIP_ALWAYS = ['id', 'requestId', 'cartItemTypeId', 'regionId', 'statusId', 'catalogSongId']

/** numberOfMeasures -> Number of measures; isMedley -> Is medley. */
const label = (key: string): string => {
    const words = key
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .trim()

    return words.charAt(0).toUpperCase() + words.slice(1)
}

const stated = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') {
        return null
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
    }
    if (typeof value === 'number') {
        return String(value)
    }
    if (typeof value === 'string') {
        return value
    }
    if (Array.isArray(value)) {
        return value.length === 0 ? null : value.map(item => String(item)).join(', ')
    }

    return null
}

/**
 * Everything the record states that the card above did not already name. An empty value prints
 * nothing at all: these entities carry dozens of columns and only a handful are filled per request,
 * so printing the blanks would bury the few that are answered.
 */
export const StatedFields = ({ data, skip = [], size = 6 }: TStatedFieldsProps) => {
    const hidden = new Set([...SKIP_ALWAYS, ...skip])

    const rows = Object.entries(data)
        .filter(([key]) => !hidden.has(key))
        .map(([key, value]) => ({ key, value: stated(value) }))
        .filter((row): row is { key: string; value: string } => row.value !== null)

    if (rows.length === 0) {
        return null
    }

    return (
        <Grid container spacing={2}>
            {rows.map(row => (
                <Grid size={size} key={row.key}>
                    <Typography variant="caption" color="text.secondary">
                        {label(row.key)}
                    </Typography>
                    <Typography variant="body1" fontWeight={300} sx={{ whiteSpace: 'pre-line' }}>
                        {row.value}
                    </Typography>
                </Grid>
            ))}
        </Grid>
    )
}
